import DOMPurify from 'dompurify';

const PURIFY = {
  USE_PROFILES: { html: true },
  ADD_ATTR: ['start'],
};

function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return escapeText(s).replace(/"/g, '&quot;');
}

/** Inline markdown → HTML for the rich editor (keeps [[cN]] as plain text). */
export function inlineMdToHtml(text: string): string {
  // Preserve explicit <u>…</u> from a prior round-trip before escaping.
  const uChunks: string[] = [];
  let raw = (text || '').replace(/<u>([\s\S]*?)<\/u>/gi, (_m, inner: string) => {
    const i = uChunks.length;
    uChunks.push(inner);
    return `\u0000U${i}\u0000`;
  });

  let s = escapeText(raw);
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label: string, href: string) => {
    return `<a href="${escapeAttr(href)}">${label}</a>`;
  });
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, '$1<em>$2</em>');
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
  s = s.replace(/\u0000U(\d+)\u0000/g, (_m, i: string) => `<u>${escapeText(uChunks[Number(i)] ?? '')}</u>`);
  return s;
}

function inlineHtmlToMd(html: string): string {
  const wrap = document.createElement('div');
  wrap.innerHTML = DOMPurify.sanitize(html || '', PURIFY);
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const inner = Array.from(el.childNodes).map(walk).join('');
    if (tag === 'strong' || tag === 'b') return `**${inner}**`;
    if (tag === 'em' || tag === 'i') return `*${inner}*`;
    if (tag === 'code') return `\`${inner}\``;
    if (tag === 'u') return `<u>${inner}</u>`;
    if (tag === 'a') {
      const href = el.getAttribute('href') || '';
      return `[${inner}](${href})`;
    }
    if (tag === 'br') return '  \n';
    return inner;
  };
  return Array.from(wrap.childNodes)
    .map(walk)
    .join('')
    .replace(/[^\S\n]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .trim();
}

function isTableSep(line: string): boolean {
  return /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line.trim());
}

/** Split a markdown table row; keeps cells that contain escaped \| . */
export function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = '';
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] === '\\' && s[i + 1] === '|') {
      cur += '|';
      i += 1;
      continue;
    }
    if (s[i] === '|') {
      cells.push(cur.trim());
      cur = '';
      continue;
    }
    cur += s[i];
  }
  cells.push(cur.trim());
  return cells;
}

export type EditorSegment =
  | { type: 'prose'; html: string }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'image'; frameId: string; caption: string };

function isBlockStart(t: string): boolean {
  return (
    !t ||
    t.startsWith('|') ||
    /^#{1,6}\s+/.test(t) ||
    /^[-*+]\s+/.test(t) ||
    /^\d+[.)]\s+/.test(t) ||
    /^!\[/.test(t)
  );
}

/** Split SOP markdown into editable segments (prose / table / screenshot). */
export function markdownToEditorSegments(body: string): EditorSegment[] {
  const lines = (body || '').replace(/\r\n/g, '\n').split('\n');
  const segments: EditorSegment[] = [];
  let proseHtml: string[] = [];
  let i = 0;

  const flushProse = () => {
    if (!proseHtml.length) return;
    segments.push({ type: 'prose', html: DOMPurify.sanitize(proseHtml.join(''), PURIFY) });
    proseHtml = [];
  };

  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (!trimmed) {
      i += 1;
      continue;
    }

    const img = trimmed.match(/^!\[([^\]]*)\]\(frame:([^)]+)\)$/);
    if (img) {
      flushProse();
      segments.push({ type: 'image', caption: img[1].trim(), frameId: img[2].trim() });
      i += 1;
      continue;
    }

    if (trimmed.startsWith('|') && i + 1 < lines.length && isTableSep(lines[i + 1].trim())) {
      flushProse();
      const headers = splitTableRow(trimmed);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && !isTableSep(lines[i].trim())) {
        rows.push(splitTableRow(lines[i]));
        i += 1;
      }
      segments.push({ type: 'table', headers, rows });
      continue;
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      proseHtml.push(`<h${level}>${inlineMdToHtml(heading[2])}</h${level}>`);
      i += 1;
      continue;
    }

    const ul = trimmed.match(/^[-*+]\s+(.*)$/);
    if (ul) {
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i].trim().match(/^[-*+]\s+(.*)$/);
        if (!m) break;
        items.push(m[1]);
        i += 1;
      }
      proseHtml.push(`<ul>${items.map((it) => `<li>${inlineMdToHtml(it)}</li>`).join('')}</ul>`);
      continue;
    }

    const ol = trimmed.match(/^(\d+)[.)]\s+(.*)$/);
    if (ol) {
      const start = parseInt(ol[1], 10) || 1;
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i].trim().match(/^(\d+)[.)]\s+(.*)$/);
        if (!m) break;
        items.push(m[2]);
        i += 1;
      }
      const startAttr = start !== 1 ? ` start="${start}"` : '';
      proseHtml.push(`<ol${startAttr}>${items.map((it) => `<li>${inlineMdToHtml(it)}</li>`).join('')}</ol>`);
      continue;
    }

    // Keep each source line as its own paragraph so line breaks are not collapsed into spaces.
    const paraLines: string[] = [];
    while (i < lines.length) {
      const t = lines[i].trim();
      if (isBlockStart(t) && t !== '') break;
      if (!t) break;
      paraLines.push(t);
      i += 1;
    }
    if (paraLines.length === 1) {
      proseHtml.push(`<p>${inlineMdToHtml(paraLines[0])}</p>`);
    } else if (paraLines.length > 1) {
      proseHtml.push(`<p>${paraLines.map((l) => inlineMdToHtml(l)).join('<br>')}</p>`);
    }
  }

  flushProse();
  return segments.length ? segments : [{ type: 'prose', html: '<p></p>' }];
}

function proseHtmlToMarkdown(html: string): string {
  const root = document.createElement('div');
  root.innerHTML = DOMPurify.sanitize(html || '', PURIFY);
  const out: string[] = [];

  const blockToMd = (el: HTMLElement): string | null => {
    const tag = el.tagName.toLowerCase();
    if (/^h[1-6]$/.test(tag)) {
      const level = Number(tag[1]);
      const t = inlineHtmlToMd(el.innerHTML);
      return t ? `${'#'.repeat(level)} ${t}` : null;
    }
    if (tag === 'p') {
      const t = inlineHtmlToMd(el.innerHTML);
      return t || null;
    }
    if (tag === 'ul') {
      const items = Array.from(el.querySelectorAll(':scope > li')).map((li) => `- ${inlineHtmlToMd((li as HTMLElement).innerHTML)}`);
      return items.join('\n') || null;
    }
    if (tag === 'ol') {
      const startAttr = parseInt(el.getAttribute('start') || '1', 10);
      const start = Number.isFinite(startAttr) && startAttr > 0 ? startAttr : 1;
      const items = Array.from(el.querySelectorAll(':scope > li')).map(
        (li, idx) => `${start + idx}. ${inlineHtmlToMd((li as HTMLElement).innerHTML)}`,
      );
      return items.join('\n') || null;
    }
    if (tag === 'div' || tag === 'span') {
      const t = inlineHtmlToMd(el.innerHTML);
      return t || null;
    }
    return inlineHtmlToMd(el.innerHTML) || null;
  };

  Array.from(root.children).forEach((child) => {
    const md = blockToMd(child as HTMLElement);
    if (md) out.push(md);
  });
  if (!out.length) {
    const t = inlineHtmlToMd(root.innerHTML);
    if (t) out.push(t);
  }
  return out.join('\n\n');
}

function tableToMarkdown(headers: string[], rows: string[][]): string {
  const esc = (c: string) => c.replace(/\|/g, '\\|');
  const head = `| ${headers.map(esc).join(' | ')} |`;
  const sep = `| ${headers.map(() => '---').join(' | ')} |`;
  const body = rows.map((r) => `| ${r.map((c) => esc(c ?? '')).join(' | ')} |`).join('\n');
  return `${head}\n${sep}\n${body}`;
}

/** Rebuild SOP markdown from editor segments. */
export function editorSegmentsToMarkdown(segments: EditorSegment[]): string {
  const parts: string[] = [];
  for (const seg of segments) {
    if (seg.type === 'prose') {
      const md = proseHtmlToMarkdown(seg.html);
      if (md) parts.push(md);
    } else if (seg.type === 'table') {
      parts.push(tableToMarkdown(seg.headers, seg.rows));
    } else if (seg.type === 'image') {
      parts.push(`![${seg.caption || ''}](frame:${seg.frameId})`);
    }
  }
  return parts.join('\n\n').trim();
}

/** Normalize markdown for stable round-trip comparisons (trim + newlines). */
export function normalizeSopMarkdown(md: string): string {
  return (md || '')
    .replace(/\r\n/g, '\n')
    .replace(/[^\S\n]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Nested sub-section stored as a `## Title` block inside a parent section body. */
export type SopBodySubsection = { title: string; body: string };

const SUBSECTION_HEADING = /^##\s+(.+?)\s*$/;

/** Split parent body into leading markdown + `##` sub-sections (does not touch `###`). */
export function splitSectionBody(markdown: string): { preamble: string; subsections: SopBodySubsection[] } {
  const lines = (markdown || '').replace(/\r\n/g, '\n').split('\n');
  const preambleLines: string[] = [];
  const subsections: SopBodySubsection[] = [];
  let current: { title: string; lines: string[] } | null = null;

  const flush = () => {
    if (!current) return;
    subsections.push({
      title: current.title,
      body: current.lines.join('\n').replace(/^\n+/, '').replace(/\n+$/, ''),
    });
    current = null;
  };

  for (const line of lines) {
    const m = SUBSECTION_HEADING.exec(line);
    if (m) {
      flush();
      current = { title: m[1].trim(), lines: [] };
      continue;
    }
    if (current) current.lines.push(line);
    else preambleLines.push(line);
  }
  flush();

  return {
    preamble: preambleLines.join('\n').replace(/\n+$/, '').replace(/^\n+/, ''),
    subsections,
  };
}

/** Rebuild parent body from preamble + sub-sections. */
export function joinSectionBody(preamble: string, subsections: SopBodySubsection[]): string {
  const parts: string[] = [];
  const p = (preamble || '').trim();
  if (p) parts.push(p);
  for (const s of subsections) {
    const title = (s.title || '').trim() || 'Untitled';
    const b = (s.body || '').trim();
    parts.push(b ? `## ${title}\n\n${b}` : `## ${title}`);
  }
  return parts.join('\n\n');
}

/** Append an empty `## title` sub-section to a parent body. */
export function appendSubsection(markdown: string, title: string): string {
  const heading = title.trim();
  if (!heading) return markdown || '';
  const { preamble, subsections } = splitSectionBody(markdown);
  return joinSectionBody(preamble, [...subsections, { title: heading, body: '' }]);
}

/** Replace one sub-section body by index; returns original markdown if index is out of range. */
export function replaceSubsectionBody(markdown: string, index: number, body: string): string {
  const { preamble, subsections } = splitSectionBody(markdown);
  if (index < 0 || index >= subsections.length) return markdown || '';
  const next = subsections.map((s, i) => (i === index ? { ...s, body } : s));
  return joinSectionBody(preamble, next);
}
